import { useState, useCallback, useRef } from "react";
import debounce from "lodash/debounce";

/**
 * Custom hook for search functionality with debouncing and date formatting.
 * @param {Object} options - Configuration options
 * @param {Function} options.onSearchChange - Optional callback called when search term changes (after debounce).
 *                                            Useful for resetting pagination when searching.
 */
export const useSearch = (options = {}) => {
  const { onSearchChange } = options;
  const onSearchChangeRef = useRef(onSearchChange);
  onSearchChangeRef.current = onSearchChange;

  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [formattedDateTerm, setFormattedDateTerm] = useState("");

  // Function to detect and format date terms
  const formatDateTerm = (term) => {
    // Skip empty terms
    if (!term) return { isDate: false, formattedTerm: term };

    // Month names (full and abbreviated)
    const months = {
      january: 1,
      jan: 1,
      february: 2,
      feb: 2,
      march: 3,
      mar: 3,
      april: 4,
      apr: 4,
      may: 5,
      june: 6,
      jun: 6,
      july: 7,
      jul: 7,
      august: 8,
      aug: 8,
      september: 9,
      sep: 9,
      sept: 9,
      october: 10,
      oct: 10,
      november: 11,
      nov: 11,
      december: 12,
      dec: 12,
    };

    // Different date patterns
    // Pattern 1: "April" (just month)
    const monthOnlyPattern =
      /^(january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|jun|jul|aug|sep|sept|oct|nov|dec)$/i;

    // Pattern 2: "April 22" or "April 22nd" (month and day)
    const monthDayPattern =
      /^(january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|jun|jul|aug|sep|sept|oct|nov|dec)[,\s]+(\d{1,2})(?:st|nd|rd|th)?$/i;

    // Pattern 3: "April 22, 2025" or "April 22 2025" (full date)
    const fullDatePattern =
      /^(january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|jun|jul|aug|sep|sept|oct|nov|dec)[,\s]+(\d{1,2})(?:st|nd|rd|th)?[,\s]+(\d{4})$/i;

    const currentYear = new Date().getFullYear();
    let formattedDate = "";

    // Case 1: Full date (Month Day, Year)
    const fullDateMatch = term.match(fullDatePattern);
    if (fullDateMatch) {
      const month = months[fullDateMatch[1].toLowerCase()];
      const day = parseInt(fullDateMatch[2], 10);
      const year = parseInt(fullDateMatch[3], 10);

      // Validate day based on month
      const lastDayOfMonth = new Date(year, month, 0).getDate();
      const validDay = Math.min(Math.max(1, day), lastDayOfMonth);

      formattedDate = `${year}-${month.toString().padStart(2, "0")}-${validDay
        .toString()
        .padStart(2, "0")}`;
      return { isDate: true, formattedTerm: formattedDate };
    }

    // Case 2: Month and day (April 22)
    const monthDayMatch = term.match(monthDayPattern);
    if (monthDayMatch) {
      const month = months[monthDayMatch[1].toLowerCase()];
      const day = parseInt(monthDayMatch[2], 10);

      // Always use current year by default
      let year = currentYear;

      // Validate day based on month
      const lastDayOfMonth = new Date(year, month, 0).getDate();
      const validDay = Math.min(Math.max(1, day), lastDayOfMonth);

      formattedDate = `${year}-${month.toString().padStart(2, "0")}-${validDay
        .toString()
        .padStart(2, "0")}`;
      return { isDate: true, formattedTerm: formattedDate };
    }

    // Case 3: Just month name (April)
    const monthOnlyMatch = term.match(monthOnlyPattern);
    if (monthOnlyMatch) {
      const month = months[monthOnlyMatch[1].toLowerCase()];

      // For month-only searches, just use current year and month (YYYY-MM)
      const year = currentYear;

      // For month-only format, we'll just use year-month format without day
      formattedDate = `${year}-${month.toString().padStart(2, "0")}`;
      return { isDate: true, formattedTerm: formattedDate };
    }

    // If no date pattern matches, return the original term
    return { isDate: false, formattedTerm: term };
  };

  const debouncedSearch = useCallback(
    debounce((term) => {
      // Check if the term is a date and format it
      const { isDate, formattedTerm } = formatDateTerm(term);

      setSearchTerm(term);
      if (isDate) {
        setFormattedDateTerm(formattedTerm);
      } else {
        setFormattedDateTerm("");
      }

      setIsSearching(false);

      // Call the onSearchChange callback if provided (e.g., to reset pagination)
      if (onSearchChangeRef.current) {
        onSearchChangeRef.current(term);
      }
    }, 500),
    []
  );

  const handleSearch = (term) => {
    setIsSearching(true);
    debouncedSearch(term);
  };

  const resetSearch = useCallback(() => {
    setSearchTerm("");
    setFormattedDateTerm("");
    setIsSearching(false);
    debouncedSearch.cancel();
  }, [debouncedSearch]);

  return {
    searchTerm,
    setSearchTerm,
    formattedDateTerm,
    isSearching,
    resetSearch,
    handleSearch,
    formatDateTerm,
  };
};
